import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

interface User {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  niche: string | null;
}

interface UserSelectProps {
  users: User[];
  selectedUsers: string[];
  onSelect: (emails: string[]) => void;
  multiple?: boolean;
}

export default function UserSelect({ users, selectedUsers, onSelect, multiple = false }: UserSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (user.display_name?.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower))
    );
  });

  const handleSelect = (email: string) => {
    if (multiple) {
      onSelect(
        selectedUsers.includes(email)
          ? selectedUsers.filter(e => e !== email)
          : [...selectedUsers, email]
      );
    } else {
      onSelect([email]);
      setIsOpen(false);
    }
  };

  const removeUser = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(selectedUsers.filter(e => e !== email));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Users Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`min-h-[48px] p-2 bg-gray-900/50 border ${
          isOpen ? 'border-[#c9fffc]' : 'border-gray-700'
        } rounded-xl cursor-pointer transition-all hover:border-[#c9fffc]/50`}
      >
        {selectedUsers.length === 0 ? (
          <div className="flex items-center text-gray-400 px-2">
            Select Recipients
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map(email => {
              const user = users.find(u => u.email === email);
              return (
                <div
                  key={email}
                  className="flex items-center gap-1 bg-[#c9fffc]/10 text-[#c9fffc] px-2 py-1 rounded-lg text-sm max-w-xs"
                >
                  <div className="truncate">
                    <div className="font-medium truncate">
                      {user?.display_name || 'Unnamed User'}
                    </div>
                    <div className="text-xs text-[#c9fffc]/70 truncate">
                      {email}
                    </div>
                  </div>
                  <button
                    onClick={(e) => removeUser(email, e)}
                    className="hover:text-red-400 transition-colors flex-shrink-0 ml-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-gray-900 border border-gray-700 rounded-xl shadow-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:border-[#c9fffc] focus:ring-1 focus:ring-[#c9fffc] text-white text-sm"
              />
            </div>
          </div>

          {/* Users List */}
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {filteredUsers.length === 0 ? (
              <div className="p-4 text-gray-400 text-center">No users found</div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  onClick={() => handleSelect(user.email)}
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors ${
                    selectedUsers.includes(user.email) ? 'bg-[#c9fffc]/10 text-[#c9fffc]' : 'text-gray-300'
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium">{user.display_name || 'Unnamed User'}</div>
                    <div className="text-sm text-gray-400">{user.email}</div>
                  </div>
                  {selectedUsers.includes(user.email) && (
                    <Check className="w-4 h-4 text-[#c9fffc] ml-2" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
